/**
 * roomCrypto — the security foundation for free-mode P2P collaboration.
 *
 * The share link IS the capability: `#room=<roomId>&k=<base64url 32-byte>`. The
 * fragment never leaves the browser. From the secret `k` we HKDF-derive three
 * independent keys so the relay (and anyone who learns a topic but not `k`)
 * can neither read nor forge room content:
 *
 *   - topic      → the GossipSub topic name (unguessable; reveals nothing)
 *   - contentKey → AES-GCM key sealing EVERY room payload (pubsub + streams)
 *   - authKey    → HMAC key for the membership handshake, bound to peer+room
 *
 * Transport encryption (Noise) protects network links; THIS protects content.
 * Group-symmetric ⇒ no per-member attribution — members are trusted with the
 * doc (a CRDT) once they hold the link; this stops non-members, not members.
 *
 * Pure WebCrypto (crypto.subtle) — runs in the browser and in Node.
 */

const HKDF_SALT = new TextEncoder().encode("wb-p2p-v1");
const KEY_BYTES = 32;
const IV_BYTES = 12; // AES-GCM standard nonce

// WebCrypto's BufferSource params are typed ArrayBufferView<ArrayBuffer>; a
// plain Uint8Array is Uint8Array<ArrayBufferLike> under TS 5.7+. Coerce at the
// crypto.subtle boundary (runtime accepts the Uint8Array fine).
function bs(u: Uint8Array): BufferSource {
  return u as unknown as BufferSource;
}

export interface RoomLink {
  readonly roomId: string;
  readonly key: Uint8Array;
  readonly link: string;
}

export interface RoomKeys {
  /** GossipSub topic name derived from the secret. */
  readonly topic: string;
  /** AES-GCM key sealing room payloads. */
  readonly contentKey: CryptoKey;
  /** HMAC key for the membership-proof handshake. */
  readonly authKey: CryptoKey;
}

// ── base64url ──────────────────────────────────────────────────────
function toB64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromB64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ── share link ─────────────────────────────────────────────────────

/** Mint a fresh room secret and a shareable link for `roomId`. */
export function mintRoomLink(roomId: string, origin = ""): RoomLink {
  const key = crypto.getRandomValues(new Uint8Array(KEY_BYTES));
  const link = `${origin}/#room=${encodeURIComponent(roomId)}&k=${toB64Url(key)}`;
  return { roomId, key, link };
}

/** Parse a `#room=…&k=…` link (href or bare fragment). Null if absent/malformed. */
export function parseRoomLink(href: string): {
  roomId: string;
  key: Uint8Array;
} | null {
  const hash = href.includes("#") ? href.slice(href.indexOf("#") + 1) : href;
  const params = new URLSearchParams(hash);
  const roomId = params.get("room");
  const k = params.get("k");
  if (!roomId || !k) return null;
  try {
    const key = fromB64Url(k);
    if (key.length !== KEY_BYTES) return null;
    return { roomId, key };
  } catch {
    return null;
  }
}

// ── key schedule ───────────────────────────────────────────────────

async function hkdf(
  key: Uint8Array,
  info: string,
  bytes: number,
): Promise<ArrayBuffer> {
  const base = await crypto.subtle.importKey("raw", bs(key), "HKDF", false, [
    "deriveBits",
  ]);
  return crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: bs(HKDF_SALT),
      info: bs(new TextEncoder().encode(info)),
    },
    base,
    bytes * 8,
  );
}

/** Derive the topic name + content/auth keys from a room secret. */
export async function deriveRoomKeys(key: Uint8Array): Promise<RoomKeys> {
  const topicBits = await hkdf(key, "wb:topic", 16);
  const contentBits = await hkdf(key, "wb:content", 32);
  const authBits = await hkdf(key, "wb:auth", 32);
  const contentKey = await crypto.subtle.importKey(
    "raw",
    contentBits,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
  const authKey = await crypto.subtle.importKey(
    "raw",
    authBits,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
  return {
    topic: `wb/${toB64Url(new Uint8Array(topicBits))}`,
    contentKey,
    authKey,
  };
}

// ── AEAD: seal every room payload ──────────────────────────────────

/** Seal `plaintext` → `iv ‖ ciphertext` (AES-GCM, random nonce per message). */
export async function seal(
  contentKey: CryptoKey,
  plaintext: Uint8Array,
): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: bs(iv) },
    contentKey,
    bs(plaintext),
  );
  const out = new Uint8Array(IV_BYTES + ct.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ct), IV_BYTES);
  return out;
}

/** Open a sealed payload, or null if it can't be authenticated (→ drop it). */
export async function open(
  contentKey: CryptoKey,
  sealed: Uint8Array,
): Promise<Uint8Array | null> {
  if (sealed.length <= IV_BYTES) return null;
  const iv = sealed.subarray(0, IV_BYTES);
  const ct = sealed.subarray(IV_BYTES);
  try {
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: bs(iv) },
      contentKey,
      bs(ct),
    );
    return new Uint8Array(pt);
  } catch {
    return null; // wrong key / tampered
  }
}

// ── membership proof: bind a session to (roomId, peerId) ───────────

function authMessage(
  roomId: string,
  peerId: string,
  challenge: Uint8Array,
): Uint8Array {
  const prefix = new TextEncoder().encode(`${roomId} ${peerId} `);
  const out = new Uint8Array(prefix.length + challenge.length);
  out.set(prefix, 0);
  out.set(challenge, prefix.length);
  return out;
}

/** HMAC proof that the holder knows `k`, bound to this (room, peer, challenge). */
export async function computeAuthProof(
  authKey: CryptoKey,
  input: { roomId: string; peerId: string; challenge: Uint8Array },
): Promise<Uint8Array> {
  const sig = await crypto.subtle.sign(
    "HMAC",
    authKey,
    bs(authMessage(input.roomId, input.peerId, input.challenge)),
  );
  return new Uint8Array(sig);
}

export async function verifyAuthProof(
  authKey: CryptoKey,
  input: {
    roomId: string;
    peerId: string;
    challenge: Uint8Array;
    proof: Uint8Array;
  },
): Promise<boolean> {
  return crypto.subtle.verify(
    "HMAC",
    authKey,
    bs(input.proof),
    bs(authMessage(input.roomId, input.peerId, input.challenge)),
  );
}
